import axios from 'axios'

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.'

// Axios puts its own wording on transport failures ("Request failed with status code 400").
// That tells the user nothing, so it never wins over the fallback.
const AXIOS_BOILERPLATE = /^(Request failed with status code|Network Error|timeout of )/

// Some backend call sites still throw `new Error(JSON.stringify(e))`, which would put a wall
// of Facebook JSON in a toast. Treat anything that looks serialized as unusable.
const looksSerialized = (message: string): boolean =>
  /^[[{]/.test(message.trim()) || message.includes('fbtrace_id')

/**
 * Pulls the most useful message out of an error from a Nest endpoint.
 *
 * Nest replies `{ statusCode, message }`, where `message` is a string for a thrown
 * HttpException (including Facebook's own copy, which survives all the way from the
 * graph call) and a string array when ValidationPipe rejects the body.
 */
export function getApiErrorMessage(err: unknown, fallback = DEFAULT_FALLBACK): string {
  const candidates: string[] = []

  if (axios.isAxiosError(err)) {
    const message = err.response?.data?.message
    if (Array.isArray(message)) {
      candidates.push(message.filter(m => typeof m === 'string').join('. '))
    } else if (typeof message === 'string') {
      candidates.push(message)
    }
  }

  if (err instanceof Error && !AXIOS_BOILERPLATE.test(err.message)) {
    candidates.push(err.message)
  }

  const usable = candidates.find(m => m.trim().length > 0 && !looksSerialized(m))
  return usable ?? fallback
}
